import { Component } from '@theme/component';

/**
 * Full-screen header menu overlay.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} closeButton - Close button inside overlay.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenuOverlay extends Component {
  requiredRefs = ['closeButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    document.addEventListener('header-menu-overlay:open', this.#onOpenRequest);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('header-menu-overlay:open', this.#onOpenRequest);
  }

  /**
   * @returns {boolean}
   */
  get isOpen() {
    return !this.hasAttribute('hidden');
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  };

  /**
   * @param {CustomEvent} event
   */
  #onOpenRequest = (event) => {
    const sectionId = event.detail?.sectionId;

    if (sectionId && sectionId !== this.dataset.sectionId) return;

    this.open();
  };

  /**
   * Open the overlay menu.
   */
  open() {
    this.removeAttribute('hidden');
    document.documentElement.classList.add('header-menu-overlay-open');
    this.refs.closeButton?.focus();
    this.#updateTriggers(true);
  }

  /**
   * Close the overlay menu.
   */
  close() {
    this.setAttribute('hidden', '');
    document.documentElement.classList.remove('header-menu-overlay-open');
    this.#updateTriggers(false);
    this.#returnFocusToTrigger();
  }

  /**
   * @param {boolean} isOpen
   */
  #updateTriggers(isOpen) {
    const triggers = document.querySelectorAll(
      `[aria-controls="${this.id}"], [data-header-menu-overlay-target="${this.id}"]`
    );

    triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  #returnFocusToTrigger() {
    const trigger = document.querySelector(`[aria-controls="${this.id}"][aria-expanded="false"]`);

    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }
  }
}

if (!customElements.get('header-menu-overlay')) {
  customElements.define('header-menu-overlay', HeaderMenuOverlay);
}

/**
 * Menu trigger button that opens the overlay.
 *
 * @extends {Component}
 */
class HeaderMenuOverlayTrigger extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    event.preventDefault();

    const overlayId = this.getAttribute('aria-controls');
    const overlay = overlayId ? document.getElementById(overlayId) : null;

    if (overlay instanceof HeaderMenuOverlay) {
      overlay.isOpen ? overlay.close() : overlay.open();
      return;
    }

    document.dispatchEvent(
      new CustomEvent('header-menu-overlay:open', {
        detail: {
          sectionId: this.dataset.sectionId,
        },
      })
    );
  };
}

if (!customElements.get('header-menu-overlay-trigger')) {
  customElements.define('header-menu-overlay-trigger', HeaderMenuOverlayTrigger);
}
